import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Loader2, Shield, LogOut, AlertTriangle, CheckCircle, XCircle,
  Database, Activity, Bell, RefreshCw, Clock, Server, AlertCircle, Wifi, Globe
} from 'lucide-react';
import { format } from 'date-fns';
import { Logo } from '../components/Logo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminSystemPage() {
  const { token, logout, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Data
  const [systemStatus, setSystemStatus] = useState(null);
  const [errors, setErrors] = useState([]);
  const [errorStats, setErrorStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState('all');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadSystemStatus(),
        loadErrors(),
        loadAlerts()
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load system data');
    } finally {
      setLoading(false);
    }
  };

  const loadSystemStatus = async () => {
    const response = await fetch(`${API_URL}/api/admin/system/status`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (response.ok) {
      const data = await response.json();
      setSystemStatus(data);
    }
  };

  const loadErrors = async () => {
    const url = categoryFilter === 'all' 
      ? `${API_URL}/api/admin/system/errors?limit=50`
      : `${API_URL}/api/admin/system/errors?limit=50&category=${categoryFilter}`;
    
    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (response.ok) {
      const data = await response.json();
      setErrors(data.errors || []);
      setErrorStats(data.stats);
    }
  };

  const loadAlerts = async () => {
    const response = await fetch(`${API_URL}/api/admin/system/alerts`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (response.ok) {
      const data = await response.json();
      setAlerts(data.alerts || []);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
    toast.success('Data refreshed');
  };

  const handleResolveError = async (errorId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/system/errors/${errorId}/resolve`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Error marked as resolved');
        loadErrors();
      }
    } catch (error) {
      toast.error('Failed to resolve error');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const getHealthBadge = (health) => {
    const variants = {
      HEALTHY: 'bg-green-100 text-green-800 border-green-300',
      DEGRADED: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      WARNING: 'bg-orange-100 text-orange-800 border-orange-300',
      CRITICAL: 'bg-red-100 text-red-800 border-red-300',
    };
    return (
      <Badge className={`${variants[health] || variants.HEALTHY} border text-sm`}>
        {health}
      </Badge>
    );
  };

  const getCategoryLabel = (category) => {
    const labels = {
      signup_provider: 'Provider Signup',
      signup_client: 'Client Signup',
      login: 'Login',
      booking_create: 'Booking Creation',
      provider_activation: 'Provider Activation',
      payment_redirect: 'Payment Redirect',
      admin_action: 'Admin Action',
      general: 'General'
    };
    return labels[category] || category;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-card border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center gap-6">
              <Logo variant="compact" size="sm" />
              <div className="border-l pl-6 flex items-center gap-3">
                <div className="bg-primary/10 p-2 rounded-lg">
                  <Server className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h1 className="text-lg font-semibold">System Monitoring</h1>
                  <p className="text-sm text-muted-foreground">Admin Dashboard</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/admin')}
              >
                <Shield className="mr-2 h-4 w-4" />
                Admin Panel
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                disabled={refreshing}
              >
                <RefreshCw className={`mr-2 h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button variant="ghost" size="sm" onClick={handleLogout}>
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* System Health Banner */}
        {systemStatus && (
          <Card className={`mb-6 ${
            systemStatus.health === 'CRITICAL' ? 'border-red-300 bg-red-50' :
            systemStatus.health === 'WARNING' ? 'border-orange-300 bg-orange-50' :
            systemStatus.health === 'DEGRADED' ? 'border-yellow-300 bg-yellow-50' :
            'border-green-300 bg-green-50'
          }`}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Activity className={`h-8 w-8 ${
                    systemStatus.health === 'HEALTHY' ? 'text-green-600' : 'text-red-600'
                  }`} />
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-xl font-semibold">System Health</h2>
                      {getHealthBadge(systemStatus.health)}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      Last checked: {format(new Date(systemStatus.last_check), 'MMM d, yyyy HH:mm:ss')}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">Environment</p>
                  <Badge variant="outline">{systemStatus.environment?.toUpperCase()}</Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* High Priority Alerts */}
        {alerts.length > 0 && (
          <Card className="mb-6 border-red-300">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-700">
                <Bell className="h-5 w-5" />
                High Priority Alerts ({alerts.length})
              </CardTitle>
              <CardDescription>Routes with repeated failures requiring attention</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {alerts.map((alert, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200">
                    <div>
                      <p className="font-medium text-red-800">{alert.route}</p>
                      <p className="text-sm text-red-600">
                        {alert.failure_count} failures in the last {alert.window_minutes} minutes
                      </p>
                    </div>
                    <Badge className={alert.severity === 'HIGH' ? 'bg-red-600' : 'bg-orange-500'}>
                      {alert.severity}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Errors</p>
                  <p className="text-2xl font-bold">{errorStats?.total_errors || 0}</p>
                </div>
                <AlertCircle className="h-8 w-8 text-muted-foreground/30" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-red-600">Failed Signups</p>
                  <p className="text-2xl font-bold text-red-600">{errorStats?.failed_signups || 0}</p>
                </div>
                <XCircle className="h-8 w-8 text-red-200" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-orange-600">Failed Bookings</p>
                  <p className="text-2xl font-bold text-orange-600">{errorStats?.failed_bookings || 0}</p>
                </div>
                <AlertTriangle className="h-8 w-8 text-orange-200" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-yellow-600">Failed Activations</p>
                  <p className="text-2xl font-bold text-yellow-600">{errorStats?.failed_activations || 0}</p>
                </div>
                <Clock className="h-8 w-8 text-yellow-200" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Database Status */}
        {systemStatus?.database && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5" />
                Database Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  <div className="flex items-center gap-2 mt-1">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="font-medium text-green-600">Connected</span>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Providers</p>
                  <p className="font-medium">{systemStatus.database.providers}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Bookings</p>
                  <p className="font-medium">{systemStatus.database.bookings}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Clients</p>
                  <p className="font-medium">{systemStatus.database.clients}</p>
                </div>
              </div>
              <div className="mt-4 p-3 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground">
                  <strong>Backup Info:</strong> {systemStatus.database.backup_info}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Connectivity & CORS — self-diagnose "network error" issues */}
        {systemStatus?.connectivity && (() => {
          const c = systemStatus.connectivity;
          const currentOrigin = typeof window !== 'undefined' ? window.location.origin : '';
          const originAllowed =
            (c.allowedOrigins || []).includes(currentOrigin) ||
            (c.corsMode === 'regex' && /(localhost|\.preview\.emergentagent\.com)/.test(currentOrigin));
          return (
            <Card className="mb-6" data-testid="connectivity-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Wifi className="h-5 w-5" />
                  Connectivity &amp; CORS
                </CardTitle>
                <CardDescription>
                  If the live site shows a "network error", check that this page's origin is allowed below.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-3 rounded-lg border bg-green-50 border-green-200" data-testid="backend-reachable">
                    <p className="text-sm text-muted-foreground">Backend</p>
                    <div className="flex items-center gap-2 mt-1">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="font-medium text-green-700">Reachable</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 break-all">{API_URL}</p>
                  </div>
                  <div className={`p-3 rounded-lg border ${originAllowed ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}
                    data-testid="origin-allowed">
                    <p className="text-sm text-muted-foreground">This page's origin</p>
                    <div className="flex items-center gap-2 mt-1">
                      {originAllowed
                        ? <CheckCircle className="h-4 w-4 text-green-600" />
                        : <XCircle className="h-4 w-4 text-red-600" />}
                      <span className={`font-medium ${originAllowed ? 'text-green-700' : 'text-red-700'}`}>
                        {originAllowed ? 'Allowed by CORS' : 'Blocked by CORS'}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 break-all">{currentOrigin}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Environment</p>
                    <Badge variant="outline" className="mt-1">{(c.environment || '').toUpperCase() || 'UNKNOWN'}</Badge>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">CORS mode</p>
                    <Badge variant="outline" className="mt-1">{c.corsMode || 'unknown'}</Badge>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">App URL</p>
                    <p className="font-medium text-sm break-all">{c.appUrl}</p>
                  </div>
                </div>

                <div>
                  <p className="text-sm text-muted-foreground flex items-center gap-1 mb-1">
                    <Globe className="h-4 w-4" /> Allowed origins
                  </p>
                  <ul className="space-y-1" data-testid="allowed-origins-list">
                    {(c.allowedOrigins || []).map((o) => (
                      <li key={o} className="text-sm font-mono bg-muted rounded px-2 py-1 break-all">{o}</li>
                    ))}
                    {c.previewRegexEnabled && (
                      <li className="text-xs text-muted-foreground italic px-2">
                        + localhost &amp; *.preview.emergentagent.com (dev/preview)
                      </li>
                    )}
                  </ul>
                </div>

                {!originAllowed && (
                  <div className="p-3 rounded-lg border border-red-200 bg-red-50 text-sm text-red-800" data-testid="cors-fix-hint">
                    <p className="font-medium">How to fix:</p>
                    <p className="mt-1">
                      This origin isn't in the allowed list, which causes the "network error".
                      Set <code className="font-mono">ENVIRONMENT=production</code> on the deployment so the live
                      domains are allowed, then redeploy. If it persists, contact Emergent Support.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })()}

        {/* Recent Errors */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5" />
                  Recent Errors
                </CardTitle>
                <CardDescription>Logged failures and exceptions</CardDescription>
              </div>
              <Select value={categoryFilter} onValueChange={(v) => { setCategoryFilter(v); }}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Filter by category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="signup_provider">Provider Signup</SelectItem>
                  <SelectItem value="signup_client">Client Signup</SelectItem>
                  <SelectItem value="login">Login</SelectItem>
                  <SelectItem value="booking_create">Booking Creation</SelectItem>
                  <SelectItem value="provider_activation">Provider Activation</SelectItem>
                  <SelectItem value="payment_redirect">Payment Redirect</SelectItem>
                  <SelectItem value="admin_action">Admin Action</SelectItem>
                  <SelectItem value="general">General</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            {errors.length === 0 ? (
              <div className="text-center py-8">
                <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
                <p className="text-muted-foreground">No errors logged. System is running smoothly.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {errors.map((error) => (
                  <div 
                    key={error.id} 
                    className={`p-4 rounded-lg border ${error.resolved ? 'bg-gray-50 border-gray-200' : 'bg-red-50 border-red-200'}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="outline" className="text-xs">
                            {getCategoryLabel(error.category)}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {format(new Date(error.timestamp), 'MMM d, HH:mm:ss')}
                          </span>
                          {error.resolved && (
                            <Badge className="bg-green-100 text-green-800 text-xs">Resolved</Badge>
                          )}
                        </div>
                        <p className="font-medium text-sm">{error.route}</p>
                        <p className="text-sm text-red-700 mt-1">{error.error_message}</p>
                        {error.user_email && (
                          <p className="text-xs text-muted-foreground mt-1">User: {error.user_email}</p>
                        )}
                      </div>
                      {!error.resolved && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleResolveError(error.id)}
                        >
                          Resolve
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            <Button 
              variant="outline" 
              className="w-full mt-4"
              onClick={loadErrors}
            >
              Load More
            </Button>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
